import { z } from 'zod';
import { conditionSchema } from './segments.js';
import { paramsSchema, type ParamSource } from './template-params.js';

/**
 * Automation flow definition. A flow is a small directed acyclic graph: one trigger, a start
 * node, and nodes that point at their successors. The same schema and validator run in the API
 * (save/publish), the worker (before executing) and the dashboard builder.
 */

export const FLOW_TRIGGERS = [
  'order.created',
  'order.paid',
  'order.fulfilled',
  'order.cancelled',
  'checkout.abandoned',
  'cart.abandoned',
  'customer.created',
  'whatsapp.message_received',
] as const;
export type FlowTrigger = (typeof FLOW_TRIGGERS)[number];

export const ORDER_TRIGGERS: readonly FlowTrigger[] = [
  'order.created',
  'order.paid',
  'order.fulfilled',
  'order.cancelled',
];

export const MAX_FLOW_NODES = 50;
export const MAX_FLOW_WAIT_MS = 30 * 24 * 3600_000;
/** Free-form WhatsApp text is only valid inside the 24-hour customer service window. */
export const SESSION_WINDOW_MS = 24 * 3600_000;

const nodeId = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_-]{0,39}$/, 'Use lower-case letters, digits, - and _');
const next = nodeId.nullable();
const char26 = z.string().length(26);

const orderCondition = z.discriminatedUnion('field', [
  z.object({
    field: z.literal('order.total'),
    op: z.enum(['gte', 'lte']),
    value: z.number().min(0).max(1_000_000_000),
  }),
  z.object({
    field: z.literal('order.paymentMethod'),
    op: z.literal('is'),
    value: z.enum(['cod', 'prepaid']),
  }),
  z.object({
    field: z.literal('order.riskLevel'),
    op: z.literal('is'),
    value: z.enum(['low', 'medium', 'high']),
  }),
  z.object({
    field: z.literal('order.codStatus'),
    op: z.literal('is'),
    value: z.enum(['none', 'pending', 'confirmed', 'declined']),
  }),
]);
export const flowConditionSchema = z.union([conditionSchema, orderCondition]);
export type FlowCondition = z.infer<typeof flowConditionSchema>;

export const flowNodeSchema = z.discriminatedUnion('type', [
  z.object({
    id: nodeId,
    type: z.literal('send_whatsapp_template'),
    phoneNumberId: char26,
    templateName: z.string().min(1).max(512),
    templateLanguage: z.string().min(2).max(20),
    params: paramsSchema,
    /** Order flows: mark the order "COD confirmation pending" so the customer's reply is read. */
    awaitCodReply: z.boolean().optional(),
    next,
  }),
  z.object({
    id: nodeId,
    type: z.literal('send_whatsapp_text'),
    phoneNumberId: char26,
    text: z.string().trim().min(1).max(1024),
    next,
  }),
  z.object({
    id: nodeId,
    type: z.literal('wait'),
    amount: z.number().int().min(1).max(43_200),
    unit: z.enum(['minutes', 'hours', 'days']),
    next,
  }),
  z.object({
    id: nodeId,
    type: z.literal('condition'),
    match: z.enum(['all', 'any']),
    conditions: z.array(flowConditionSchema).min(1).max(10),
    yes: next,
    no: next,
  }),
  z.object({
    id: nodeId,
    type: z.literal('split'),
    /** Share of runs (1-99 %) that take path A; the rest take B. Stable for a given run. */
    percentA: z.number().int().min(1).max(99),
    a: next,
    b: next,
  }),
  z.object({
    id: nodeId,
    type: z.enum(['add_tag', 'remove_tag']),
    tag: z.string().trim().min(1).max(50),
    next,
  }),
]);
export type FlowNode = z.infer<typeof flowNodeSchema>;

export const flowGraphSchema = z.object({
  trigger: z.enum(FLOW_TRIGGERS),
  start: nodeId.nullable(),
  nodes: z.array(flowNodeSchema).max(MAX_FLOW_NODES),
});
export type FlowGraph = z.infer<typeof flowGraphSchema>;

export const EMPTY_FLOW: FlowGraph = { trigger: 'order.created', start: null, nodes: [] };

export interface FlowIssue {
  nodeId: string | null;
  code:
    | 'no_start'
    | 'unknown_start'
    | 'unknown_target'
    | 'duplicate_id'
    | 'cycle'
    | 'unreachable'
    | 'too_long_wait'
    | 'order_field_without_order'
    | 'text_outside_window'
    | 'no_action';
  message: string;
}

export const waitMs = (n: Extract<FlowNode, { type: 'wait' }>) =>
  n.amount * (n.unit === 'minutes' ? 60_000 : n.unit === 'hours' ? 3600_000 : 24 * 3600_000);

/** Successors of a node, in a stable order. */
export function successors(n: FlowNode): string[] {
  if (n.type === 'condition') return [n.yes, n.no].filter((x): x is string => x !== null);
  if (n.type === 'split') return [n.a, n.b].filter((x): x is string => x !== null);
  return n.next === null ? [] : [n.next];
}

const usesOrderField = (n: FlowNode): boolean => {
  if (n.type === 'send_whatsapp_template') {
    return n.params.some((p: ParamSource) => p.source.startsWith('order.'));
  }
  if (n.type === 'condition') return n.conditions.some((c) => c.field.startsWith('order.'));
  return false;
};

/**
 * Structural validation of an already schema-valid graph. Pure and synchronous: references to
 * phone numbers and templates are checked separately against the database at publish time.
 * A draft may have issues; publishing requires none.
 */
export function validateFlow(graph: FlowGraph): FlowIssue[] {
  const issues: FlowIssue[] = [];
  const byId = new Map<string, FlowNode>();
  for (const n of graph.nodes) {
    if (byId.has(n.id)) {
      issues.push({
        nodeId: n.id,
        code: 'duplicate_id',
        message: `Node id "${n.id}" is used twice`,
      });
    }
    byId.set(n.id, n);
  }

  if (graph.start === null) {
    if (graph.nodes.length > 0) {
      issues.push({ nodeId: null, code: 'no_start', message: 'Choose where the flow starts' });
    }
  } else if (!byId.has(graph.start)) {
    issues.push({ nodeId: null, code: 'unknown_start', message: 'The start node does not exist' });
  }

  for (const n of graph.nodes) {
    for (const t of successors(n)) {
      if (!byId.has(t)) {
        issues.push({
          nodeId: n.id,
          code: 'unknown_target',
          message: `Node "${n.id}" points at "${t}", which does not exist`,
        });
      }
    }
  }
  if (issues.some((i) => i.code === 'duplicate_id' || i.code === 'unknown_target')) return issues;

  // Cycle detection (DFS), then the worst-case wait before each node (DP in topological order;
  // memoised, so diamond-shaped graphs stay linear).
  const waitBefore = new Map<string, number>();
  const state = new Map<string, 1 | 2>();
  const postorder: string[] = [];
  let acyclic = true;
  const dfs = (id: string) => {
    state.set(id, 1);
    for (const t of successors(byId.get(id)!)) {
      if (state.get(t) === 1) {
        if (acyclic) {
          issues.push({
            nodeId: t,
            code: 'cycle',
            message: 'Flows cannot loop back to an earlier step',
          });
        }
        acyclic = false;
      } else if (!state.has(t)) dfs(t);
    }
    state.set(id, 2);
    postorder.push(id);
  };
  if (graph.start !== null && byId.has(graph.start)) {
    dfs(graph.start);
    if (acyclic) {
      waitBefore.set(graph.start, 0);
      for (const id of [...postorder].reverse()) {
        const n = byId.get(id)!;
        const after = (waitBefore.get(id) ?? 0) + (n.type === 'wait' ? waitMs(n) : 0);
        for (const t of successors(n)) waitBefore.set(t, Math.max(waitBefore.get(t) ?? 0, after));
      }
    }
  }

  if (graph.start !== null && byId.has(graph.start) && acyclic) {
    for (const n of graph.nodes) {
      if (!waitBefore.has(n.id)) {
        issues.push({
          nodeId: n.id,
          code: 'unreachable',
          message: `Node "${n.id}" cannot be reached from the start`,
        });
      }
    }
    for (const n of graph.nodes) {
      const total = (waitBefore.get(n.id) ?? 0) + (n.type === 'wait' ? waitMs(n) : 0);
      if (total > MAX_FLOW_WAIT_MS) {
        issues.push({
          nodeId: n.id,
          code: 'too_long_wait',
          message: 'Waits on one path add up to more than 30 days',
        });
      }
      if (n.type === 'send_whatsapp_text') {
        const inWindow =
          graph.trigger === 'whatsapp.message_received' &&
          (waitBefore.get(n.id) ?? 0) < SESSION_WINDOW_MS;
        if (!inWindow) {
          issues.push({
            nodeId: n.id,
            code: 'text_outside_window',
            message:
              'Free text can only follow a received WhatsApp message within 24 hours; use a template',
          });
        }
      }
    }
  }

  if (!ORDER_TRIGGERS.includes(graph.trigger)) {
    for (const n of graph.nodes) {
      if (usesOrderField(n)) {
        issues.push({
          nodeId: n.id,
          code: 'order_field_without_order',
          message: 'Order fields are only available when the flow starts from an order event',
        });
      }
    }
  }

  if (
    graph.nodes.length > 0 &&
    !graph.nodes.some((n) => n.type === 'send_whatsapp_template' || n.type === 'send_whatsapp_text')
  ) {
    issues.push({ nodeId: null, code: 'no_action', message: 'The flow never sends a message' });
  }
  return issues;
}

// ── recipes ─────────────────────────────────────────────────────────────────

export const FLOW_RECIPES = [
  'abandoned_checkout_recovery',
  'abandoned_checkout_two_reminders',
  'abandoned_cart_recovery',
  'post_purchase_thank_you',
  'best_shopping_experience',
  'cod_confirmation',
] as const;
export type FlowRecipeId = (typeof FLOW_RECIPES)[number];

export interface FlowRecipeInput {
  phoneNumberId: string;
  templateName: string;
  templateLanguage: string;
  params: ParamSource[];
}

export const FLOW_RECIPE_INFO: Record<
  FlowRecipeId,
  { title: string; trigger: FlowTrigger; summary: string }
> = {
  abandoned_checkout_recovery: {
    title: 'Abandoned checkout recovery',
    trigger: 'checkout.abandoned',
    summary:
      'One hour after a checkout is abandoned, send your template and tag the customer. Stops by itself if the customer completes the order.',
  },
  abandoned_checkout_two_reminders: {
    title: 'Abandoned checkout: two reminders',
    trigger: 'checkout.abandoned',
    summary:
      'A reminder after 1 hour and a last one after another 24 hours. Both are skipped once the customer orders.',
  },
  abandoned_cart_recovery: {
    title: 'Abandoned cart recovery',
    trigger: 'cart.abandoned',
    summary: 'Two hours after a cart is abandoned, send your template and tag the customer.',
  },
  post_purchase_thank_you: {
    title: 'Post-purchase thank you',
    trigger: 'order.paid',
    summary: 'A day after an order is paid, send your template and tag the customer.',
  },
  cod_confirmation: {
    title: 'COD confirmation',
    trigger: 'order.created',
    summary:
      'For cash-on-delivery orders: ask the customer to confirm, tag risky orders, and send one reminder after 6 hours if there is no answer. Replies like "confirm" or "cancel" are read automatically and shown on the order.',
  },
  best_shopping_experience: {
    title: 'Best shopping experience (full journey)',
    trigger: 'order.paid',
    summary:
      'Instant order message, then COD and prepaid customers are tagged, repeat buyers are told apart from first-timers, and a week later half the customers get a follow-up (A) and half a second variant (B) to compare. Uses one template for every message; change each step afterwards.',
  },
};

/** The starting graph of a recipe, filled with the template the merchant picked. */
export function buildRecipeGraph(id: FlowRecipeId, input: FlowRecipeInput): FlowGraph {
  const send = (nodeId: string, nextId: string | null): FlowNode => ({
    id: nodeId,
    type: 'send_whatsapp_template',
    phoneNumberId: input.phoneNumberId,
    templateName: input.templateName,
    templateLanguage: input.templateLanguage,
    params: input.params,
    next: nextId,
  });
  const wait = (
    nodeId: string,
    amount: number,
    unit: 'minutes' | 'hours' | 'days',
    nextId: string,
  ): FlowNode => ({ id: nodeId, type: 'wait', amount, unit, next: nextId });
  const tag = (nodeId: string, name: string): FlowNode => ({
    id: nodeId,
    type: 'add_tag',
    tag: name,
    next: null,
  });
  const trigger = FLOW_RECIPE_INFO[id].trigger;

  switch (id) {
    case 'abandoned_checkout_recovery':
      return {
        trigger,
        start: 'wait-1',
        nodes: [
          wait('wait-1', 1, 'hours', 'send-1'),
          send('send-1', 'tag-1'),
          tag('tag-1', 'recovery-sent'),
        ],
      };
    case 'abandoned_checkout_two_reminders':
      return {
        trigger,
        start: 'wait-1',
        nodes: [
          wait('wait-1', 1, 'hours', 'send-1'),
          send('send-1', 'wait-2'),
          wait('wait-2', 24, 'hours', 'send-2'),
          send('send-2', 'tag-1'),
          tag('tag-1', 'recovery-sent'),
        ],
      };
    case 'abandoned_cart_recovery':
      return {
        trigger,
        start: 'wait-1',
        nodes: [
          wait('wait-1', 2, 'hours', 'send-1'),
          send('send-1', 'tag-1'),
          tag('tag-1', 'cart-reminded'),
        ],
      };
    case 'post_purchase_thank_you':
      return {
        trigger,
        start: 'wait-1',
        nodes: [
          wait('wait-1', 1, 'days', 'send-1'),
          send('send-1', 'tag-1'),
          tag('tag-1', 'thanked'),
        ],
      };
    case 'cod_confirmation': {
      const ask = send('send-1', 'wait-1');
      return {
        trigger,
        start: 'check-cod',
        nodes: [
          {
            id: 'check-cod',
            type: 'condition',
            match: 'all',
            conditions: [{ field: 'order.paymentMethod', op: 'is', value: 'cod' }],
            yes: 'check-risk',
            no: null,
          },
          {
            id: 'check-risk',
            type: 'condition',
            match: 'all',
            conditions: [{ field: 'order.riskLevel', op: 'is', value: 'high' }],
            yes: 'tag-risk',
            no: 'send-1',
          },
          { id: 'tag-risk', type: 'add_tag', tag: 'rto-risk-high', next: 'send-1' },
          ask.type === 'send_whatsapp_template' ? { ...ask, awaitCodReply: true } : ask,
          wait('wait-1', 6, 'hours', 'check-reply'),
          {
            id: 'check-reply',
            type: 'condition',
            match: 'all',
            conditions: [{ field: 'order.codStatus', op: 'is', value: 'pending' }],
            yes: 'send-2',
            no: null,
          },
          send('send-2', 'tag-unconfirmed'),
          tag('tag-unconfirmed', 'cod-unconfirmed'),
        ],
      };
    }
    case 'best_shopping_experience': {
      const tagThen = (nodeId: string, name: string, nextId: string): FlowNode => ({
        id: nodeId,
        type: 'add_tag',
        tag: name,
        next: nextId,
      });
      return {
        trigger,
        start: 'send-1',
        nodes: [
          // 1. Right away: confirm the order.
          send('send-1', 'check-payment'),
          // 2. Know how they pay.
          {
            id: 'check-payment',
            type: 'condition',
            match: 'all',
            conditions: [{ field: 'order.paymentMethod', op: 'is', value: 'cod' }],
            yes: 'tag-cod',
            no: 'tag-prepaid',
          },
          tagThen('tag-cod', 'cod-customer', 'wait-1'),
          tagThen('tag-prepaid', 'prepaid-customer', 'wait-1'),
          // 3. Give the parcel time to arrive.
          wait('wait-1', 3, 'days', 'check-repeat'),
          // 4. Repeat buyers are treated differently from first-timers.
          {
            id: 'check-repeat',
            type: 'condition',
            match: 'all',
            conditions: [{ field: 'ordersCount', op: 'gte', value: 2 }],
            yes: 'tag-repeat',
            no: 'tag-first',
          },
          tagThen('tag-repeat', 'repeat-buyer', 'wait-2'),
          tagThen('tag-first', 'first-time-buyer', 'wait-2'),
          wait('wait-2', 4, 'days', 'split-1'),
          // 5. Follow-up, with two variants to compare.
          { id: 'split-1', type: 'split', percentA: 50, a: 'send-2', b: 'send-3' },
          send('send-2', 'tag-done'),
          send('send-3', 'tag-done'),
          tag('tag-done', 'journey-complete'),
        ],
      };
    }
  }
}
